// ────────────────────────────────────────────────────────────────────────────
// Is a delivery leg's POD accounted for?
//
// Two things can answer yes: the leg's own `podStatus` (a signature captured
// against it), or a POD DOCUMENT attached to the order. The document route is
// DERIVED — attaching a scan never writes the leg's status. Deleting the
// document, or correcting a type that was set to POD by mistake, therefore
// takes the answer back with it, and a genuine signature can never be
// overwritten or invented.
//
// MIRRORED IN C# by `InvoiceApprovalEvaluator` (My.Tms), which applies the same
// rule to the invoicing `pod` check. The two must agree: a screen that says the
// paperwork is in while the invoicing queue says it is missing is worse than
// either answer on its own.
// ────────────────────────────────────────────────────────────────────────────
import type { OrderDocument, OrderTask } from '@my/tms-api';

/**
 * Which legs the order's POD documents cover.
 *
 * <p>
 * A document carrying an `orderTaskId` covers that leg alone. One with none is
 * order-level and covers EVERY delivery leg: the operator attached proof of
 * delivery to this job, and on the single-drop orders that are the common case
 * there is nothing else it could mean. Per-leg precision stays available for
 * anyone who needs it — the column has been on `OrderDocument` from the start.
 * </p>
 */
export interface PodCoverage {
  /** An order-level POD document exists — it covers every delivery leg. */
  coversOrder: boolean;
  /** Legs covered by a POD document attached to them specifically. */
  taskIds: readonly string[];
}

export const NO_POD_COVERAGE: PodCoverage = { coversOrder: false, taskIds: [] };

export function podCoverageFrom(documents: readonly OrderDocument[] | undefined): PodCoverage {
  if (!documents || documents.length === 0) return NO_POD_COVERAGE;
  let coversOrder = false;
  const taskIds: string[] = [];
  for (const doc of documents) {
    if (doc.type !== 'Pod') continue;
    if (doc.orderTaskId) taskIds.push(doc.orderTaskId);
    else coversOrder = true;
  }
  return { coversOrder, taskIds };
}

/** Whether a POD document accounts for this leg. */
export function isPodCovered(taskId: string, coverage: PodCoverage): boolean {
  return coverage.coversOrder || coverage.taskIds.includes(taskId);
}

/**
 * How a leg's POD stands, or null when nothing accounts for it yet.
 *
 * `signed` outranks `uploaded`: a captured signature is the stronger fact, and
 * saying "Uploaded" over it would understate what is on file.
 */
export type PodState = 'signed' | 'uploaded';

export function podStateFor(
  task: Pick<OrderTask, 'id' | 'podStatus' | 'podSignedAt'>,
  coverage: PodCoverage,
): PodState | null {
  if (task.podStatus === 'Signed' || task.podSignedAt) return 'signed';
  if (isPodCovered(task.id, coverage)) return 'uploaded';
  return null;
}
